/**
 * Responsibility: what the research corpus holds, against what exists.
 *
 * One read of the corpus database on the box. Nothing is fetched from any
 * source, so refreshing is free and the button is not a warning.
 *
 * **The headline figures are examples *and* measurements, together.** An
 * example is a picture; a measurement is a picture with a grader's own
 * numbers beside it, and only the second can calibrate anything. The corpus
 * holds far more of the first than the second, and a page that showed only
 * examples would suggest the calibration set is orders of magnitude larger
 * than it is.
 *
 * **What this tab deliberately does not claim.** Whether last night's
 * archive succeeded, and whether Nextcloud is reachable. Those belong to
 * the backup job rather than to the corpus, and a green tick here would be
 * a claim this route cannot support - so the note says where to look
 * instead of inventing one.
 */
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQuery } from '@tanstack/react-query';
import { MetricCard } from '@kit/components';
import { hubApi } from '../../api/client';
import { DataHubBreakdown } from './DataHubBreakdown';
import { DataHubSets } from './DataHubSets';
import { readableBytes } from './dataHubFormat';

export function DataHubCard() {
  const hub = useQuery({ queryKey: ['admin', 'datahub'], queryFn: hubApi.dataHub });
  const totals = hub.data?.totals;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">Research corpus</Typography>
              <Typography variant="body2" color="text.secondary">
                The card images and grader measurements the calibration work reads
                from. One read of the box; nothing is fetched, so refreshing costs
                nothing.
              </Typography>
            </Box>
            <Tooltip title="Read it again">
              <span>
                <IconButton
                  size="small"
                  disabled={hub.isFetching}
                  onClick={() => void hub.refetch()}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Divider />

          {hub.isLoading ? <LinearProgress /> : null}
          {hub.isError ? (
            <Alert severity="error">
              The corpus could not be read. The analysis service may be offline.
            </Alert>
          ) : null}

          {totals ? (
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              }}
            >
              <MetricCard
                label="Examples"
                value={totals.examples.toLocaleString()}
                helper="cards harvested"
              />
              <MetricCard
                label="Measured"
                value={totals.measurements.toLocaleString()}
                helper="with a grader's own numbers"
              />
              <MetricCard
                label="Images"
                value={totals.images.toLocaleString()}
                helper="photographs recorded"
              />
              <MetricCard
                label="On disk"
                value={readableBytes(totals.bytes)}
                helper="before the archive prunes"
              />
            </Box>
          ) : null}

          {totals && totals.measurements < totals.examples / 10 ? (
            <Alert severity="info">
              {totals.measurements.toLocaleString()} of {totals.examples.toLocaleString()}{' '}
              examples carry a grader&apos;s own numbers. Only those can calibrate a
              measurement; the rest are pictures waiting for truth to arrive.
            </Alert>
          ) : null}

          {hub.data ? <DataHubSets sets={hub.data.sets} /> : null}
        </Stack>
      </Paper>

      {hub.data ? (
        <DataHubBreakdown categories={hub.data.categories} grades={hub.data.grades} />
      ) : null}

      <Typography variant="caption" color="text.secondary">
        Whether last night&apos;s archive ran, and whether Nextcloud is reachable, are
        the backup job&apos;s to report rather than this one&apos;s. Ask the box:{' '}
        <code>python -m hub.backup check</code> for the connection,{' '}
        <code>list</code> for what is restorable.
      </Typography>
    </Stack>
  );
}
